import React, { useState, useEffect, useCallback } from 'react';
import {
  AppLayout,
  ContentLayout,
  Header,
  SpaceBetween,
  Table,
  Button,
  Box,
  Modal,
  FormField,
  Input,
  Alert,
  Select,
  Textarea
} from '@amzn/awsui-components-core';
import { HomePageSideNavigationV2 } from 'src/components/side-navigation-v2/HomePageSideNavigationV2';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { LeaderEAConfig, LeaderEAConfigService } from '../services/LeaderEAConfigService';
import { BrassGroupType } from 'src/services/BrassService';
import { Employee, AttendeePriority } from '../interfaces/interface';
import { EmployeeSearchBar } from '../components/attendees/EmployeeSearchBar';
import { EmployeeListItem } from '../components/attendees/EmployeeListItem';
import { LeaderCell } from '../components/attendees/LeaderCell';
import { useEmployeeByAlias } from '../hooks/useEmployeeByAlias';
import { logger } from 'src/logger';

/**
 * Read-only inline employee row used in the Edit Leader modal — same visual
 * treatment as the Add Leader confirmation row (full EmployeeListItem) but
 * without a remove button, since the leader alias is the row key and can't
 * be changed.
 */
const LeaderEmployeeRow: React.FC<{ alias: string }> = ({ alias }) => {
  const employee = useEmployeeByAlias(alias);
  if (employee) {
    return <EmployeeListItem employee={employee} disableHover />;
  }
  return <Input value={alias} onChange={() => undefined} disabled />;
};

const EAHuddleAdminPage: React.FC = () => {
  const { Alias } = useAuth();
  const [configs, setConfigs] = useState<LeaderEAConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [sortingColumn, setSortingColumn] = useState<{ sortingField: string }>({ sortingField: 'leaderAlias' });
  const [sortingDescending, setSortingDescending] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<LeaderEAConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // Form fields
  const [leaderAlias, setLeaderAlias] = useState('');
  const [eaEmailAddress, setEaEmailAddress] = useState('');
  const [groupId, setGroupId] = useState('');
  const [groupType, setGroupType] = useState('');
  const [overrideMembers, setOverrideMembers] = useState('');

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<LeaderEAConfig | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Selected leader employee for visual feedback during Add Leader (search-driven)
  const [selectedLeaderEmployee, setSelectedLeaderEmployee] = useState<Employee | null>(null);

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await LeaderEAConfigService.getAll(true); // always fetch fresh in admin context
      setConfigs(data); // sorting handled by Table component
    } catch (err: unknown) {
      logger.error('Failed to load leader EA configs', { error: err });
      setError('Failed to load configurations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const resetForm = () => {
    setLeaderAlias('');
    setEaEmailAddress('');
    setGroupType('');
    setGroupId('');
    setOverrideMembers('');
    setSelectedLeaderEmployee(null);
  };

  const handleAdd = () => {
    resetForm();
    setEditingConfig(null);
    setModalError(null);
    setShowModal(true);
  };

  const handleEdit = (config: LeaderEAConfig) => {
    setLeaderAlias(config.leaderAlias);
    setEaEmailAddress(config.eaEmailAddress ?? '');
    setGroupType(config.groupType ?? '');
    setGroupId(config.groupId ?? '');
    setOverrideMembers(config.overrideMembers?.join(', ') ?? '');
    setEditingConfig(config);
    setModalError(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    setModalError(null);
    if (!leaderAlias.trim()) {
      setModalError('Leader alias is required.');
      return;
    }
    // Block creating a duplicate config — direct admin to edit the existing one instead.
    // Skipped in edit mode (editingConfig is non-null and the alias input is locked).
    if (!editingConfig) {
      const trimmedAlias = leaderAlias.trim().toLowerCase();
      if (configs.some((c) => c.leaderAlias.toLowerCase() === trimmedAlias)) {
        setModalError(`A configuration already exists for ${trimmedAlias}. Edit the existing entry instead of creating a duplicate.`);
        return;
      }
    }
    if (eaEmailAddress.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(eaEmailAddress.trim())) {
      setModalError('Invalid email address format.');
      return;
    }
    if ((groupType && !groupId.trim()) || (!groupType && groupId.trim())) {
      setModalError('Group Type and Group ID must both be set or both be empty.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const config: LeaderEAConfig = {
        leaderAlias: leaderAlias.trim().toLowerCase(),
        eaEmailAddress: eaEmailAddress.trim() || null,
        groupType: groupType || null,
        groupId: groupId.trim() || null,
        overrideMembers: overrideMembers
          .split(',')
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean),
        lastUpdatedBy: Alias,
        lastUpdatedAt: new Date().toISOString()
      };

      await LeaderEAConfigService.save(config);
      setShowModal(false);
      setSuccessMessage(`Saved configuration for ${leaderAlias}.`);
      await loadConfigs();
    } catch (err: unknown) {
      logger.error('Failed to save leader EA config', { error: err });
      setModalError('Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const alias = deleteTarget.leaderAlias;
    setDeleting(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await LeaderEAConfigService.delete(alias);
      setDeleteTarget(null);
      setSuccessMessage(`Deleted configuration for ${alias}.`);
      await loadConfigs();
    } catch (err: unknown) {
      logger.error('Failed to delete leader EA config', { error: err });
      setError('Failed to delete configuration.');
    } finally {
      setDeleting(false);
    }
  };

  const columnDefinitions = [
    {
      id: 'leaderAlias',
      header: 'Leader Alias',
      cell: (item: LeaderEAConfig) => <LeaderCell alias={item.leaderAlias} />,
      sortingField: 'leaderAlias'
    },
    {
      id: 'eaEmailAddress',
      header: 'Group Email',
      cell: (item: LeaderEAConfig) => item.eaEmailAddress ?? '—'
    },
    {
      id: 'groupType',
      header: 'Group Type',
      cell: (item: LeaderEAConfig) => item.groupType ?? '—'
    },
    {
      id: 'groupId',
      header: 'Group ID',
      cell: (item: LeaderEAConfig) => item.groupId ?? '—'
    },
    {
      id: 'overrideMembers',
      header: 'Overridden Group Members',
      cell: (item: LeaderEAConfig) => (item.overrideMembers?.length ? item.overrideMembers.join(', ') : '—')
    },
    {
      id: 'lastUpdatedBy',
      header: 'Updated By',
      cell: (item: LeaderEAConfig) => (item.lastUpdatedBy ? <LeaderCell alias={item.lastUpdatedBy} /> : '—'),
      sortingField: 'lastUpdatedBy'
    },
    {
      id: 'lastUpdatedAt',
      header: 'Updated On',
      cell: (item: LeaderEAConfig) => (item.lastUpdatedAt ? new Date(item.lastUpdatedAt).toLocaleString() : '—'),
      sortingField: 'lastUpdatedAt'
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (item: LeaderEAConfig) => (
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="icon" iconName="edit" ariaLabel="Edit" onClick={() => handleEdit(item)} />
          <Button variant="icon" iconName="remove" ariaLabel="Delete" onClick={() => setDeleteTarget(item)} />
        </SpaceBetween>
      )
    }
  ];

  return (
    <AppLayout
      navigation={<HomePageSideNavigationV2 />}
      navigationOpen={navigationOpen}
      onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
      contentType="table"
      maxContentWidth={Number.MAX_VALUE}
      toolsHide
      content={
        <ContentLayout header={<Header variant="h1">Leader to EA Group Configuration</Header>}>
          <SpaceBetween size="l">
            {error && (
              <Alert type="error" dismissible onDismiss={() => setError(null)}>
                {error}
              </Alert>
            )}
            {successMessage && (
              <Alert type="success" dismissible onDismiss={() => setSuccessMessage(null)}>
                {successMessage}
              </Alert>
            )}

            <Table
              columnDefinitions={columnDefinitions}
              items={[...configs].sort((a, b) => {
                const field = sortingColumn.sortingField as keyof LeaderEAConfig;
                const aVal = (a[field] ?? '') as string;
                const bVal = (b[field] ?? '') as string;
                const cmp = aVal.localeCompare(bVal);
                return sortingDescending ? -cmp : cmp;
              })}
              loading={loading}
              loadingText="Loading configurations..."
              empty={
                <Box textAlign="center" padding="l">
                  No leader EA configurations found. Click &quot;Add Leader&quot; to create one.
                </Box>
              }
              header={
                <Header
                  variant="h2"
                  actions={
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button iconName="refresh" onClick={loadConfigs} disabled={loading}>
                        Refresh
                      </Button>
                      <Button variant="primary" onClick={handleAdd}>
                        Add Leader
                      </Button>
                    </SpaceBetween>
                  }
                  counter={`(${configs.length})`}
                >
                  Leader to EA Mappings
                </Header>
              }
              sortingColumn={sortingColumn}
              sortingDescending={sortingDescending}
              onSortingChange={({ detail }) => {
                setSortingColumn(detail.sortingColumn as { sortingField: string });
                setSortingDescending(detail.isDescending ?? false);
              }}
              variant="full-page"
            />

            {/* Add/Edit Modal */}
            {showModal && (
              <Modal
                visible={true}
                onDismiss={() => setShowModal(false)}
                header={editingConfig ? 'Edit Leader EA Configuration' : 'Add Leader EA Configuration'}
                size="large"
                footer={
                  <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button variant="link" onClick={() => setShowModal(false)}>
                        Cancel
                      </Button>
                      <Button variant="primary" onClick={handleSave} loading={saving} disabled={!leaderAlias.trim()}>
                        Save
                      </Button>
                    </SpaceBetween>
                  </Box>
                }
              >
                <SpaceBetween size="m">
                  {modalError && <Alert type="error">{modalError}</Alert>}
                  <FormField label="Leader Alias">
                    {editingConfig ? (
                      <LeaderEmployeeRow alias={leaderAlias} />
                    ) : selectedLeaderEmployee ? (
                      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                        <EmployeeListItem employee={selectedLeaderEmployee} disableHover />
                        <Button
                          variant="icon"
                          iconName="close"
                          ariaLabel="Remove selected leader"
                          onClick={() => {
                            setSelectedLeaderEmployee(null);
                            setLeaderAlias('');
                          }}
                        />
                      </SpaceBetween>
                    ) : (
                      <EmployeeSearchBar
                        onAddAttendee={(employee: Employee, _priority: AttendeePriority) => {
                          setSelectedLeaderEmployee(employee);
                          setLeaderAlias(employee.login_name);
                        }}
                        existingEmails={new Set()}
                        singleAddButton={true}
                        addButtonText="Select"
                        placeholder="Search by name or alias..."
                        fullWidth={true}
                      />
                    )}
                  </FormField>

                  <FormField label="Group Email Address">
                    <Input
                      value={eaEmailAddress}
                      onChange={({ detail }) => setEaEmailAddress(detail.value)}
                      placeholder="Shared EA mailbox (e.g., team-ea@amazon.com)"
                    />
                  </FormField>

                  <FormField label="Group Type">
                    <Select
                      selectedOption={
                        groupType
                          ? {
                              label: groupType === BrassGroupType.AntGroup ? 'ANT Group' : groupType === 'elmo' ? 'ELMO' : groupType,
                              value: groupType
                            }
                          : { label: 'None', value: '' }
                      }
                      onChange={({ detail }) => setGroupType(detail.selectedOption.value ?? '')}
                      options={[
                        { label: 'None', value: '' },
                        { label: 'ANT Group', value: BrassGroupType.AntGroup },
                        { label: 'ELMO', value: 'elmo' }
                      ]}
                      placeholder="Select group type"
                    />
                  </FormField>

                  <FormField label="Group ID">
                    <Input
                      value={groupId}
                      onChange={({ detail }) => setGroupId(detail.value)}
                      placeholder="Group name for access checks (leave empty if none)"
                    />
                  </FormField>

                  <FormField label="Group Members (Override)">
                    <Textarea
                      value={overrideMembers}
                      onChange={({ detail }) => setOverrideMembers(detail.value)}
                      placeholder="Comma-separated logins to override (keep empty for default)"
                      rows={2}
                    />
                  </FormField>
                </SpaceBetween>
              </Modal>
            )}

            {/* Delete Confirmation Modal */}
            {deleteTarget && (
              <Modal
                visible={true}
                onDismiss={() => setDeleteTarget(null)}
                header="Confirm Deletion"
                footer={
                  <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button variant="link" onClick={() => setDeleteTarget(null)}>
                        Cancel
                      </Button>
                      <Button variant="primary" onClick={handleDelete} loading={deleting}>
                        Delete
                      </Button>
                    </SpaceBetween>
                  </Box>
                }
              >
                Are you sure you want to delete the configuration for <strong>{deleteTarget.leaderAlias}</strong>? This action cannot be undone.
              </Modal>
            )}
          </SpaceBetween>
        </ContentLayout>
      }
    />
  );
};

export default EAHuddleAdminPage;
